import { useI18n } from '@/context/I18nContext';

interface SplitPointsProps {
  splitPositions: number[];
  onRemove: (index: number) => void;
  onClearAll: () => void;
}

export function SplitPoints({ splitPositions, onRemove, onClearAll }: SplitPointsProps) {
  const { t } = useI18n();
  const hasSplits = splitPositions.length > 0;
  const totalSectors = Math.max(3, splitPositions.length + 1);

  function sectorHue(idx: number): number {
    return (idx * 360 / totalSectors) % 360;
  }

  return (
    <div className="flex items-center justify-center gap-2 p-2 rounded-2xl sm:rounded-full bg-bg-raised/80 backdrop-blur-md border border-border-subtle/80 shadow-lg flex-wrap sm:flex-nowrap w-full">
      {Array.from({ length: totalSectors }, (_, si) => {
        const isActive = si < splitPositions.length + 1;
        const canDelete = isActive && si > 0;
        const hue = sectorHue(si);

        return (
          <div
            key={si}
            className="flex-1 min-w-[70px] flex items-center justify-center gap-1.5 py-1.5 px-3 min-h-[36px] rounded-full transition-all"
            style={{
              background: isActive
                ? `hsla(${hue}, 70%, 55%, 0.14)`
                : 'var(--color-bg-surface)',
              border: isActive
                ? `1px solid hsla(${hue}, 70%, 55%, 0.35)`
                : '1px solid var(--color-border-subtle)',
              opacity: isActive ? 1 : 0.5,
            }}
          >
            <span
              className="w-2 h-2 rounded-full shrink-0 transition-colors"
              style={{
                backgroundColor: isActive
                  ? `hsl(${hue}, 70%, 55%)`
                  : 'var(--color-text-muted)',
              }}
            />
            <span
              className="text-[0.75rem] font-semibold transition-colors mono"
              style={{
                color: isActive
                  ? `hsl(${hue}, 70%, 65%)`
                  : 'var(--color-text-muted)',
              }}
            >
              S{si + 1}
            </span>
            <button
              onClick={() => canDelete && onRemove(si - 1)}
              disabled={!canDelete}
              className="w-5 h-5 flex items-center justify-center shrink-0 rounded-full text-[0.65rem] transition-all ml-0.5 disabled:opacity-0 bg-bg-hover text-text-muted hover:bg-red/20 hover:text-red cursor-pointer"
              title={canDelete ? t('common.remove') : undefined}
              aria-label={canDelete ? `Remove split ${si}` : undefined}
            >
              ✕
            </button>
          </div>
        );
      })}
      <button
        onClick={onClearAll}
        disabled={!hasSplits}
        className="flex-none sm:flex-1 min-w-[80px] text-[0.7rem] font-medium rounded-full px-3 py-1.5 min-h-[36px] flex items-center justify-center transition-all text-text-muted bg-bg-surface border border-border-subtle hover:text-red hover:border-red/30 hover:bg-red/10 cursor-pointer"
        style={{
          opacity: hasSplits ? 1 : 0.4,
          pointerEvents: hasSplits ? 'auto' : 'none',
        }}
      >
        {t('splitPoints.clearAll')}
      </button>
    </div>
  );
}
